import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SchoolIcon from "@mui/icons-material/School";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InsightsIcon from "@mui/icons-material/Insights";
import EmailIcon from "@mui/icons-material/Email";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type Lang = "en" | "zh";

const LANG_KEY = "eap-guide-lang";

const GuidePage: React.FC = () => {
  const [lang, setLang] = useState<Lang>(
    () => (localStorage.getItem(LANG_KEY) as Lang) || "en"
  );
  const [expanded, setExpanded] = useState<string | false>(false);

  // Allow deep links like /teacher/guide#enroll-students
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      setExpanded(hash);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, []);

  const changeLang = (_: unknown, value: Lang | null) => {
    if (value) {
      setLang(value);
      localStorage.setItem(LANG_KEY, value);
    }
  };

  // Tiny bilingual helper
  const t = (en: React.ReactNode, zh: React.ReactNode) =>
    lang === "en" ? en : zh;

  const handleAccordion =
    (panel: string) => (_: unknown, isExpanded: boolean) =>
      setExpanded(isExpanded ? panel : false);

  const steps: { en: string; zh: string }[] = [
    {
      en: "Sign in at app.eap-space.com/auth/sign-in",
      zh: "登录 app.eap-space.com/auth/sign-in",
    },
    {
      en: "Create your classroom (one time, takes 2 minutes)",
      zh: "创建班级（一次性操作，约 2 分钟）",
    },
    {
      en: "Students register themselves — share the sign-up link with them",
      zh: "学生自行注册——请将注册链接发给学生",
    },
    {
      en: "Add students to your classroom once they appear in your list",
      zh: "学生出现在您的列表后，将他们添加进班级",
    },
    {
      en: "Track their progress from your dashboard",
      zh: "在控制面板跟踪学生的学习进度",
    },
  ];

  const sectionTitle = (icon: React.ReactNode, text: React.ReactNode) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("Teacher Guide", "教师使用指南")}
        </Typography>
        <ToggleButtonGroup
          value={lang}
          exclusive
          onChange={changeLang}
          size="small"
        >
          <ToggleButton value="en">English</ToggleButton>
          <ToggleButton value="zh">中文</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {lang === "zh" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          系统界面为英文。本指南保留英文按钮名称并附中文解释，方便您对照操作。
        </Alert>
      )}

      {/* ---------- 1. Getting started checklist ---------- */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t("Getting started — 5 steps", "快速上手——5 步清单")}
          </Typography>
          {steps.map((step, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.8 }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: 14,
                  bgcolor: "primary.main",
                }}
              >
                {i + 1}
              </Avatar>
              <Typography variant="body2">{t(step.en, step.zh)}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* ---------- 2. Creating a classroom ---------- */}
      <Accordion
        id="create-classroom"
        expanded={expanded === "create-classroom"}
        onChange={handleAccordion("create-classroom")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <SchoolIcon color="primary" />,
            t("Creating a classroom", "创建班级")
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t(
              <>
                From your dashboard, click the <b>+ (Add)</b> button.
              </>,
              <>
                在控制面板点击 <b>+（Add，添加）</b>按钮。
              </>
            )}
          </Typography>

          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t("Field", "字段")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t("What to choose", "如何填写")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Program</TableCell>
                <TableCell>
                  {t(
                    <>
                      ⚠️ Must match your students' placement result (most
                      students place into <b>YELLOW</b>). Students only appear
                      in your add-list if the program matches.
                    </>,
                    <>
                      ⚠️ 必须与学生的分级测试结果一致（大多数学生分入{" "}
                      <b>YELLOW</b>）。项目不一致时，学生不会出现在您的添加列表中。
                    </>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Classroom Name</TableCell>
                <TableCell>
                  {t(
                    'Anything you like — e.g. "BNU Yellow — Oct 2026"',
                    '自由填写，例如 "BNU Yellow — Oct 2026"'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Start Date</TableCell>
                <TableCell>
                  {t(
                    "When the course begins for this class",
                    "本班课程开始的日期"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pace</TableCell>
                <TableCell>
                  {t(
                    "How fast weeks unlock — see below",
                    "每周内容解锁的速度——见下方说明"
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Typography variant="subtitle2" gutterBottom>
            {t("Pace options", "Pace（节奏）选项")}
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    <b>3 days</b> — a new study week unlocks every 3 days
                    (intensive)
                  </>,
                  <>
                    <b>3 days（3 天）</b>——每 3 天解锁一个新学习周（强化型）
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    <b>Weekly</b> — one study week per calendar week
                    (recommended for most classes)
                  </>,
                  <>
                    <b>Weekly（每周）</b>——每个自然周解锁一个学习周（推荐大多数班级使用）
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    <b>Fortnightly</b> — a new week every 14 days
                  </>,
                  <>
                    <b>Fortnightly（每两周）</b>——每 14 天解锁一个新学习周
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    <b>No lock — self-paced</b> — all 20 weeks open
                    immediately; students study in any order
                  </>,
                  <>
                    <b>No lock — self-paced（不锁定·自主进度）</b>——全部 20
                    周立即开放，学生可按任意顺序学习
                  </>
                )}
              </Typography>
            </li>
          </Box>

          <Alert severity="info">
            {t(
              "The End Date is calculated automatically. You can create more than one classroom — for example one per class group, or one per program.",
              "结束日期（End Date）会自动计算。您可以创建多个班级——例如每个教学班一个，或每个课程项目一个。"
            )}
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ---------- 3. Enrolling students ---------- */}
      <Accordion
        id="enroll-students"
        expanded={expanded === "enroll-students"}
        onChange={handleAccordion("enroll-students")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <GroupAddIcon color="primary" />,
            t("Enrolling students", "添加学生入班")
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              <>
                <b>Students register themselves first.</b> You don't create
                student accounts — you add already-registered students to your
                classroom.
              </>,
              <>
                <b>学生需要先自行注册。</b>
                您不需要为学生创建账号——您的操作是把已注册的学生添加进班级。
              </>
            )}
          </Alert>

          <Typography variant="body2" sx={{ mb: 1 }}>
            {t(
              "A student will only appear in your add-list after they have:",
              "学生满足以下两个条件后，才会出现在您的添加列表中："
            )}
          </Typography>
          <Box component="ol" sx={{ mt: 0, mb: 2, pl: 3 }}>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    Created their account at <b>app.eap-space.com</b> (Sign up
                    as Student) — and selected <b>your school</b> from the
                    school list
                  </>,
                  <>
                    在 <b>app.eap-space.com</b> 自行注册账号（以学生身份 Sign
                    up），并在学校列表中选择<b>您所在的学校</b>
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    Completed the <b>placement test</b> (about 20 minutes).
                    Their result decides their program level.
                  </>,
                  <>
                    完成<b>分级测试（placement test）</b>（约 20
                    分钟）。测试结果决定其课程级别。
                  </>
                )}
              </Typography>
            </li>
          </Box>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {t(
              <>
                Then, in your classroom's <b>student management</b> screen:
                tick the students you want and click{" "}
                <b>Add selected students</b>.
              </>,
              <>
                之后，进入班级的<b>学生管理</b>页面：勾选要添加的学生，点击{" "}
                <b>Add selected students（添加所选学生）</b>。
              </>
            )}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            {t("Why can't I see my student? ⚠️", "为什么看不到我的学生？⚠️")}
          </Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t("Problem", "问题")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t("Fix", "解决办法")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {t(
                    "Student hasn't finished the placement test",
                    "学生没有完成分级测试"
                  )}
                </TableCell>
                <TableCell>
                  {t(
                    "Ask them to sign in and complete the test",
                    "请学生登录并完成测试"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t(
                    'Student picked the wrong school (or "other") when registering',
                    '学生注册时选错了学校（或选了 "other"）'
                  )}
                </TableCell>
                <TableCell>
                  {t(
                    "Contact support to correct their school",
                    "联系管理员修正学校信息"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t(
                    "Your classroom's program doesn't match their placement result",
                    "您班级的 Program 与学生的分级结果不一致"
                  )}
                </TableCell>
                <TableCell>
                  {t(
                    "Check their result; create a classroom of the matching program",
                    "核对学生的分级结果，创建对应项目的班级"
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Alert severity="info">
            {t(
              "You can only see students registered under your own school.",
              "您只能看到注册在本校名下的学生。"
            )}
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ---------- 4. Course content ---------- */}
      <Accordion
        id="course-content"
        expanded={expanded === "course-content"}
        onChange={handleAccordion("course-content")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <MenuBookIcon color="primary" />,
            t("Course content", "课程内容概览")
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t(
              "Each program has 20 study weeks (ACCESS: 12). Every week contains 7 modules:",
              "每个课程项目包含 20 个学习周（ACCESS 为 12 周）。每周包含 7 个模块："
            )}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {[
              ["Reading", "阅读"],
              ["Listening", "听力"],
              ["Speaking", "口语"],
              ["Writing", "写作"],
              ["Grammar", "语法"],
              ["Vocabulary", "词汇"],
              ["Review", "复习"],
            ].map(([en, zh]) => (
              <Chip
                key={en}
                label={lang === "en" ? en : `${en} ${zh}`}
                size="small"
              />
            ))}
          </Box>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t(
              "Each module has learning content followed by a quiz.",
              "每个模块包含学习内容和一个小测验（quiz）。"
            )}
          </Typography>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            {t(
              <>
                <b>Lesson prep:</b> you can preview every lesson and quiz{" "}
                <b>with answers included</b> — open Content Preview from the
                menu. Students never see the answers; this view is teachers
                only.
              </>,
              <>
                <b>备课功能：</b>您可以预览每一课和每个测验，
                <b>并且可以看到答案</b>——在菜单中打开 Content
                Preview（内容预览）。学生看不到答案，此视图仅教师可见。
              </>
            )}
          </Alert>
          <Typography variant="body2">
            {t(
              "Students see the current unlocked week plus everything before it. Locked future weeks show as locked (unless your classroom is self-paced).",
              "学生可以看到当前已解锁的学习周及之前的全部内容。未解锁的周显示为锁定状态（自主进度班级除外）。"
            )}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ---------- 5. Tracking progress ---------- */}
      <Accordion
        id="track-progress"
        expanded={expanded === "track-progress"}
        onChange={handleAccordion("track-progress")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <InsightsIcon color="primary" />,
            t("Tracking student progress", "跟踪学生进度")
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t(
              "From your classroom dashboard you can see:",
              "在班级控制面板中您可以查看："
            )}
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            {[
              [
                "Per-student progress — which week and module each student is on",
                "每位学生的进度——当前学到第几周、哪个模块",
              ],
              [
                "Quiz results — scores, attempts, and time spent per quiz",
                "测验成绩——分数、尝试次数、每次测验用时",
              ],
              [
                "Class analytics — how the whole class performs, module by module",
                "全班分析——按模块查看全班整体表现",
              ],
              [
                "Inactive students — who hasn't logged in recently",
                "不活跃学生——近期未登录、可能需要提醒的学生",
              ],
            ].map(([en, zh], i) => (
              <li key={i}>
                <Typography variant="body2">{t(en, zh)}</Typography>
              </li>
            ))}
          </Box>
          <Alert severity="info">
            {t(
              "Check the dashboard weekly. A student stuck on the same module for two weeks usually needs help, not more time.",
              "建议每周查看一次控制面板。如果某学生在同一模块停留两周，通常是遇到了困难，需要的是帮助而不是时间。"
            )}
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ---------- 6. Emailing students ---------- */}
      <Accordion
        id="email-students"
        expanded={expanded === "email-students"}
        onChange={handleAccordion("email-students")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <EmailIcon color="primary" />,
            t("Emailing your students", "给学生发邮件")
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t(
                  <>
                    <b>Enrollment emails</b> — when adding students, tick the
                    email option and they receive a "you're enrolled" email
                    with a sign-in link
                  </>,
                  <>
                    <b>注册确认邮件</b>
                    ——添加学生时勾选发送邮件选项，学生会收到"您已入班"的邮件，内含登录链接
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    <b>Email All / Select for Email</b> — compose your own
                    message (announcements, reminders) and send it to all or
                    selected students. Emails come from no-reply@eap-space.com.
                  </>,
                  <>
                    <b>Email All（群发）/ Select for Email（选择收件人）</b>
                    ——自行撰写内容（通知、提醒等），发送给全班或所选学生。邮件发件人为
                    no-reply@eap-space.com。
                  </>
                )}
              </Typography>
            </li>
          </Box>
          <Alert severity="warning">
            {t(
              "Remind students using QQ Mail / 163 Mail to check their spam folder for emails from EAP Space.",
              "请提醒使用 QQ 邮箱 / 163 邮箱的学生：EAP Space 的邮件可能进入垃圾邮件文件夹，请注意查收。"
            )}
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ---------- 7. Your account ---------- */}
      <Accordion
        id="your-account"
        expanded={expanded === "your-account"}
        onChange={handleAccordion("your-account")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <AccountCircleIcon color="primary" />,
            t("Your account", "账号管理")
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t(
                  <>
                    <b>First sign-in:</b> use the temporary password you were
                    given; you'll be asked to set your own (min 8 characters,
                    with uppercase, lowercase, number and symbol)
                  </>,
                  <>
                    <b>首次登录：</b>
                    使用收到的临时密码登录后，系统会要求您设置自己的密码（至少 8
                    位，须包含大写字母、小写字母、数字和符号）
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t(
                  <>
                    <b>Forgot password:</b> on the sign-in page, click{" "}
                    <b>Forgot password</b> — a verification code is emailed to
                    you
                  </>,
                  <>
                    <b>忘记密码：</b>在登录页点击 <b>Forgot
                    password（忘记密码）</b>，验证码会发送到您的邮箱
                  </>
                )}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t(
                  <>
                    <b>Profile photo:</b> update it from your profile page
                  </>,
                  <>
                    <b>头像：</b>可在个人资料页更新
                  </>
                )}
              </Typography>
            </li>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ---------- 8. FAQ ---------- */}
      <Accordion
        id="faq"
        expanded={expanded === "faq"}
        onChange={handleAccordion("faq")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {sectionTitle(
            <HelpOutlineIcon color="primary" />,
            t("FAQ", "常见问题")
          )}
        </AccordionSummary>
        <AccordionDetails>
          {[
            [
              "Can a student be in two classrooms?",
              "No. Each student belongs to exactly one classroom at a time. Moving a student between classrooms affects their progress record — contact support if you need this.",
              "一个学生可以加入两个班级吗？",
              "不可以。每个学生同一时间只属于一个班级。转班会影响学习记录，如有需要请联系管理员。",
            ],
            [
              "Can I change the pace after creating the classroom?",
              "Changing pace affects newly added students. For students already enrolled, contact support.",
              "创建班级后可以修改 Pace（节奏）吗？",
              "修改后仅对新添加的学生生效。已在班学生的节奏调整请联系管理员。",
            ],
            [
              "A student's name is spelled wrong.",
              "Name changes are handled by the admin (names appear on certificates). Send the correction to your program coordinator.",
              "学生姓名拼写有误怎么办？",
              "姓名修改由管理员处理（姓名将印在证书上）。请把更正信息发给您的项目协调人。",
            ],
            [
              "The student says a week is locked but shouldn't be.",
              'Check your classroom\'s pace and start date — weeks unlock on schedule from the start date. For self-paced access, ask your coordinator about the "No lock" option.',
              "学生说某一周被锁定，但按进度应该已解锁。",
              '请检查班级的 Pace 和开始日期——学习周按开始日期起的节奏依次解锁。如需全部开放，请向协调人咨询 "No lock（不锁定）" 选项。',
            ],
          ].map(([qEn, aEn, qZh, aZh], i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{t(qEn, qZh)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(aEn, aZh)}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* ---------- Help footer ---------- */}
      <Alert severity="info" sx={{ mt: 3 }}>
        {t(
          <>
            Need help? Email <b>support@eap-space.com</b> or contact your
            program coordinator directly.
          </>,
          <>
            需要帮助？发邮件至 <b>support@eap-space.com</b>
            ，或直接联系您的项目协调人。
          </>
        )}
      </Alert>
    </Box>
  );
};

export default GuidePage;
